import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { Cpu, Lightbulb } from "lucide-react";
import { formatDate } from "@/lib/dateFormat";

/* Client portal — visual progress feed of prototype iterations.
 * Pulls from /api/client/projects/{pid}/iterations (gated to physical_product projects). */
export default function ClientIterationFeed({ projectId }) {
  const [data, setData] = useState({ items: [], project_name: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/client/projects/${projectId}/iterations`);
      setData(data);
    } catch (e) { logger.debug("iteration feed load failed", e?.message || e); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!data.items.length) return null;  // hide on non-hardware projects

  return (
    <section className="bg-white rounded-sm border border-slate-100" data-testid="client-iteration-feed">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <span className="w-7 h-7 rounded-sm bg-[#F54800] text-white flex items-center justify-center"><Cpu size={13} /></span>
        <div>
          <div className="label-overline">Prototype journey</div>
          <div className="font-display text-xl font-bold text-slate-900 mt-0.5">From foam mock to finished product</div>
        </div>
      </div>
      <div className="p-5 space-y-5">
        {data.items.map((it, i) => (
          <article key={it.id} className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4" data-testid={`iter-feed-${it.id}`}>
            <div className="relative">
              {it.image_url ? (
                <img src={it.image_url} alt={`Prototype ${it.version}`} loading="lazy"
                  className="w-full aspect-[4/3] object-cover rounded-sm bg-slate-100" />
              ) : (
                <div className="w-full aspect-[4/3] bg-slate-50 rounded-sm flex items-center justify-center text-slate-300">
                  <Cpu size={28} />
                </div>
              )}
              <div className="absolute top-2 left-2 bg-[#F54800] text-white text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-sm">
                {it.version || `V${data.items.length - i}`}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                {it.date ? formatDate(it.date) : "Recently"}
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900 mt-1">{it.version || `Iteration ${data.items.length - i}`}</h3>
              {it.summary && (
                <p className="text-sm text-slate-700 mt-2 leading-relaxed whitespace-pre-wrap">{it.summary}</p>
              )}
              {it.learnings && (
                <div className="mt-3 bg-amber-50 border-l-4 border-l-amber-400 px-3 py-2 rounded-sm flex items-start gap-2" data-testid={`iter-feed-learning-${it.id}`}>
                  <Lightbulb size={13} className="text-amber-700 shrink-0 mt-0.5" />
                  <div className="text-[12px] text-amber-900 leading-relaxed">
                    <span className="font-bold">What we learned · </span>
                    <span className="whitespace-pre-wrap">{it.learnings}</span>
                  </div>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
